import {
  Box,
  CircularProgress,
  Flex,
  Hide,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { RiFileTextLine } from "react-icons/ri";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { DocViewerControls } from "./DocViewerControls";
import { ProxyRenderer } from "./renderers/ProxyRenderer";
import { DocViewerProvider } from "./state";

type Props = {
  title: string;
  uri?: string;
  file?: File;
  isOpen: boolean;
  onClose: () => void;
  onOpen?: () => void;
};

export const DocViewer = ({ title, uri, file, isOpen, onClose }: Props) => {
  const [contentType, setContentType] = useState<string>();
  const [documentData, setDocumentData] = useState<ArrayBuffer>();

  useEffect(() => {
    if (file) {
      file.arrayBuffer().then((data) => {
        setDocumentData(data);
        setContentType(file.type);
      });
    } else if (uri) {
      fetch(uri, {
        method: "GET",
      })
        .then((response) => {
          const contentTypeRaw = response.headers.get("content-type");
          const contentTypes = contentTypeRaw?.split(";") || [];
          const contentType = contentTypes.length ? contentTypes[0] : undefined;
          setContentType(contentType);

          return response.arrayBuffer();
        })
        .then((documentData) => {
          setDocumentData(documentData);
        });
    }
  }, [uri, setDocumentData, file]);

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      scrollBehavior="inside"
      isCentered={false}
      size="full"
    >
      <ModalOverlay />
      <ModalContent
        width="100vw"
        height="100vh"
        m={0}
        maxW="100vw"
        maxHeight="100vh"
        borderRadius={0}
        background="transparent"
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
      >
        <DocViewerProvider>
          <ModalHeader
            as={Flex}
            display="flex"
            gap={2}
            fontSize="md"
            fontWeight="400"
            alignItems="center"
            background="white"
            position="relative"
            zIndex={1}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: { lg: "33vw" },
              }}
            >
              <RiFileTextLine />
              <TextWithOverflowTooltip
                content={title}
                hasArrow
                flex="1"
                minWidth="0"
                maxWidth="100%"
              />
            </Box>
            <Hide below="lg">
              <DocViewerControls uri={uri} />
            </Hide>
            <ModalCloseButton top="none" />
          </ModalHeader>
          <ModalBody p="2" flex="1" overflow="hidden" position="relative">
            <Box
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {documentData ? (
                <ProxyRenderer
                  contentType={contentType}
                  documentData={documentData}
                />
              ) : (
                <CircularProgress isIndeterminate color="primary.500" />
              )}
            </Box>
          </ModalBody>
          <Hide above="lg">
            <ModalFooter
              justifyContent="center"
              background="white"
              position="relative"
              zIndex={1}
            >
              <DocViewerControls uri={uri} />
            </ModalFooter>
          </Hide>
        </DocViewerProvider>
      </ModalContent>
    </Modal>
  );
};
